import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import Landing from "@/pages/Landing";
import { Login, Signup, ResetPassword } from "@/pages/Auth";
import DriverSignup from "@/pages/DriverSignup";
import DriverReapply from "@/pages/DriverReapply";
import ApplicationStatus from "@/pages/ApplicationStatus";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import AppShell from "@/pages/AppShell";
import { ShipperDashboard, NewRequest } from "@/pages/marketplace/ShipperPages";
import { DriverDashboard, MyBids } from "@/pages/marketplace/DriverPages";
import { RequestDetail } from "@/pages/marketplace/RequestDetail";
import { Profile } from "@/pages/marketplace/Profile";
import { DriversDirectory, DriverProfilePage } from "@/pages/DriversPages";
import { Billing, BillingSuccess, BillingCancel, KycPage, AdminKyc } from "@/pages/BillingKycPages";
import AdminUsers from "@/pages/AdminUsers";
import AdminRequests from "@/pages/AdminRequests";
import AdminEmail from "@/pages/AdminEmail";
import { AdminReferrals } from "@/pages/AdminReferrals";
import { AdminSso } from "@/pages/AdminSso";
import { ProfitCalculator, CalcCallback } from "@/pages/ProfitCalculator";
import { DriverPayout } from "@/pages/DriverPayout";
import { Referrals } from "@/pages/Referrals";
import CalendarPage from "@/pages/CalendarPage";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import SmoothScroll from "@/components/SmoothScroll";

function AppEntry() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-zinc-500 text-xs uppercase tracking-widest animate-pulse">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "driver") return <Navigate to="/app/driver" replace />;
  return <ShipperDashboard />;
}

// Authenticated users landing on /app/login (bookmarked link) go straight to
// their role home — logged-out visitors never render this: AppShell bounces
// them to the real sign-in card at /login first.
function DeepLinkLogin() {
  const { user } = useAuth();
  if (!user) return null;
  const dest = user.role === "driver" ? "/app/driver" : user.role === "admin" ? "/app/admin/users" : "/app";
  return <Navigate to={dest} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <SmoothScroll>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/driver-signup" element={<DriverSignup />} />
              <Route path="/driver-reapply" element={<DriverReapply />} />
              <Route path="/application-status" element={<ApplicationStatus />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/auth/callback" element={<CalcCallback />} />
              <Route path="/profit-calculator" element={<ProfitCalculator />} />
              <Route path="/drivers" element={<DriversDirectory />} />
              <Route path="/drivers/:id" element={<DriverProfilePage />} />
              {/* legacy leads console — admin work happens under /app/admin now */}
              <Route path="/admin" element={<Navigate to="/app/admin/users" replace />} />

              {/* Authenticated app */}
              <Route path="/app" element={<AppShell />}>
                <Route index element={<AppEntry />} />
                <Route path="new" element={<NewRequest />} />
                <Route path="driver" element={<DriverDashboard />} />
                <Route path="bids" element={<MyBids />} />
                <Route path="calendar" element={<CalendarPage />} />
                <Route path="requests/:id" element={<RequestDetail />} />
                <Route path="profile" element={<Profile />} />
                <Route path="kyc" element={<KycPage />} />
                <Route path="billing" element={<Billing />} />
                <Route path="billing/success" element={<BillingSuccess />} />
                <Route path="billing/cancel" element={<BillingCancel />} />
                <Route path="admin/kyc" element={<AdminKyc />} />
                <Route path="admin/users" element={<AdminUsers />} />
                <Route path="admin/requests" element={<AdminRequests />} />
                <Route path="admin/email" element={<AdminEmail />} />
                <Route path="admin/referrals" element={<AdminReferrals />} />
                <Route path="admin/sso" element={<AdminSso />} />
                <Route path="payout" element={<DriverPayout />} />
                <Route path="refer" element={<Referrals />} />
                {/* /app/login + any /app/* typo stay inside the app instead of
                    falling through to the global catch-all (landing page) */}
                <Route path="login" element={<DeepLinkLogin />} />
                <Route path="*" element={<Navigate to="/app" replace />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </SmoothScroll>
        </BrowserRouter>
      </AuthProvider>
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: {
            background: "#121212",
            border: "1px solid #27272A",
            borderRadius: 0,
            color: "#FFFFFF",
            fontFamily: "Inter, sans-serif",
          },
        }}
      />
    </div>
  );
}

export default App;
